import { Fragment, useContext, useEffect, useEffectEvent, useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { useParams, useSearchParams } from 'react-router'
import { Close } from '@mui/icons-material'
import { Alert, CircularProgress, Drawer, Grid, IconButton } from '@mui/material'
import { useSnackbar } from 'notistack'

import OrderDetails from '../../../../components/orders/order-details'
import { AppContext, useHost } from '../../../../providers/app-provider'
import Api from '../../../../utils/api'
import useSharedStyles from '../../../../utils/shared.styles'
import AccountsContext from '../../context'

import useStyles from './index.styles'
import OrderRow from './order-row'

const TabPanelOrders = () => {
  const { accountId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { folder, folderAttributes, searchText } = useContext(AccountsContext)
  const { pageRefreshCounter } = useContext(AppContext)
  const auth = useAuth()
  const host = useHost()
  const openOrderId = searchParams.get('orderId')

  const classes = useStyles
  const sharedClasses = useSharedStyles
  const { enqueueSnackbar } = useSnackbar()

  const [orderItems, setOrderItems] = useState<IOrderListItem[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const filteredOrders = useMemo(() => {
    let results = orderItems
    if (searchText) {
      results = results
        .filter(
          (item) =>
            item.shortOrderNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
            item.rmaNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
            item.discountCode?.toLowerCase().includes(searchText.toLowerCase()) ||
            item.trackingNumber?.toLowerCase().includes(searchText.toLowerCase()) ||
            item.shipToAddress?.shipToEmail.toLowerCase().includes(searchText.toLowerCase()) ||
            item.shipToAddress?.shipToPhone
              .replace(/[-()\s]/g, '')
              .toLowerCase()
              .includes(searchText.toLowerCase().replace(/[-()\s]/g, '')) ||
            item.orderNodeId.toString().includes(searchText)
        )
        .sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))
    }
    return results.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))
  }, [orderItems, searchText])

  const modalOpen = Boolean(openOrderId)

  const fetchOrders = useEffectEvent(async () => {
    if (!host || !auth.user?.access_token) return
    setLoading(true)
    const resOrders = await Api.get(`${host}/api/v2/central/folders/${accountId}/orders`)
    setLoading(false)

    if (resOrders.ok) {
      setOrderItems(resOrders.body.orders.map((order: IOrderListItem) => ({ ...order, orderType: order.disposition === 'scrap' ? 'scrap' : order.orderType })))
    } else {
      enqueueSnackbar('Something went wrong and we could not load the orders for this account', {
        variant: 'error',
      })
    }
  })
  useEffect(() => {
    fetchOrders()
  }, [accountId, host, pageRefreshCounter])

  const closeModal = () => {
    setSearchParams({ tab: 'orders' })
  }

  return (
    <Grid container={true} size={12}>
      {loading && (
        <Grid container={true} size={12} sx={[sharedClasses.centerContentHorizontal, classes.loadingWrapper]}>
          <CircularProgress />
        </Grid>
      )}
      {!loading && (
        <Grid size={12} data-testid="orders">
          {filteredOrders.length === 0 && <Alert severity="info">No orders found on this account</Alert>}
          {filteredOrders.map((orderItem: IOrderListItem) => (
            <Fragment key={orderItem.orderNodeId}>
              <OrderRow key={orderItem.orderNodeId} orderItem={orderItem} />

              {openOrderId === String(orderItem.orderNodeId) ? (
                <Drawer
                  open={modalOpen}
                  onClose={closeModal}
                  anchor="bottom"
                  ModalProps={{ sx: classes.orderModal }}
                  slotProps={{
                    paper: { sx: classes.orderPaper },
                  }}
                >
                  <IconButton sx={classes.orderModalCloseButton} onClick={closeModal} aria-label="Close Order Modal">
                    <Close />
                  </IconButton>
                  {folder && folderAttributes && (
                    <OrderDetails
                      folderId={folder.id}
                      folderName={folder?.name}
                      accountNumber={folderAttributes.clientAccountNumber}
                      orderType={orderItem.orderType}
                    />
                  )}
                </Drawer>
              ) : null}
            </Fragment>
          ))}
        </Grid>
      )}
    </Grid>
  )
}

export default TabPanelOrders
